'use client';

import { c as _c } from "react/compiler-runtime";
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Chat, useChat } from '@ai-sdk/react';
import { DefaultChatTransport, type UIMessage } from 'ai';
import { useTRPCClient } from '~/modules/trpc/client';
import { getChatLocale } from './chat-locale-store';

/**
 * Module-scoped registry of `Chat` instances keyed by thread id. Sharing a single
 * `Chat` per thread lets multiple components (chat panel, mobile composer, drawer
 * header) reflect the same `messages` / `status` state without a context provider.
 *
 * Threads start empty and hydrate their transcript asynchronously from Postgres
 * (FUN-4637) via `useThreadHydration` — reopening a "Recent chat" shows the
 * original conversation again. The draft chat (`__draft__`) is in-memory only.
 */
/**
 * `Chat` exposes `status`/`error` as read-only getters (only `setStatus` is
 * available, and it's `protected`). `promoteDraftChat` needs to copy the draft's
 * terminal error onto the promoted thread, so this thin subclass surfaces a
 * public setter for that one case.
 */
class ActivityAiChat extends Chat<UIMessage> {
  applyTerminalError(error: Error | undefined): void {
    this.setStatus({
      status: 'error',
      error
    });
  }
}
const chatRegistry = new Map<string, ActivityAiChat>();

/**
 * Thread ids whose transcript is already in memory — either hydrated from
 * Postgres or freshly promoted from the draft. The hydrator skips these so it
 * never refetches or clobbers a live transcript with a stale/empty DB read.
 */
const hydratedThreads = new Set<string>();
function getOrCreateChat(id: string): ActivityAiChat {
  const existing = chatRegistry.get(id);
  if (existing) return existing;
  const chat = new ActivityAiChat({
    id,
    messages: [],
    transport: new DefaultChatTransport<UIMessage>({
      api: '/api/ai/chat',
      // `regenerate` (and any other helper that doesn't pass an explicit
      // `body`) would otherwise omit `chatId`, which the route requires —
      // backfill it from the thread id so retries don't 400.
      prepareSendMessagesRequest: ({
        id: chatId,
        body,
        messages,
        trigger,
        messageId
      }) => ({
        // `activeFilters` (when present) is supplied by the caller's `body` — set at
        // submit time from the live filter state — so a follow-up keeps the
        // drawer-edited filters (the model re-applies them).
        body: {
          chatId,
          // Default the reply language to the site locale so paths that
          // don't set it (regenerate/edit) still pin correctly. An
          // explicit caller `body.locale` (normal sends) overrides via spread.
          locale: getChatLocale() ?? undefined,
          ...body,
          messages,
          trigger,
          messageId
        }
      })
    })
    // Persistence is server-side now (the `/api/ai/chat` route's onFinish).
    // The chat intentionally does NOT mirror the AI's `appliedVatMode` onto
    // the page-wide VAT toggle: the model sometimes set `vatMode` even when
    // the user never mentioned VAT, silently flipping "BTW inclusief" (FUN-4558
    // MVP2 feedback). The VAT toggle stays user-controlled.
  });
  chatRegistry.set(id, chat);
  return chat;
}

/** Drop a thread's in-memory state so a deleted chat can't resurrect from cache. */
export function removeChatFromRegistry(id: string): void {
  chatRegistry.delete(id);
  hydratedThreads.delete(id);
}

/**
 * Wraps `@ai-sdk/react`'s `useChat` for the activity search experience. Pass the
 * active thread id; passing `null` returns helpers backed by a shared "draft" chat
 * that is promoted to a real thread once the user sends a first message.
 */
export function useActivityAiChat(threadId) {
  const $ = _c(8);
  const t0 = threadId ?? "__draft__";
  let t1;
  if ($[0] !== t0) {
    t1 = getOrCreateChat(t0);
    $[0] = t0;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const chat = t1;
  let t2;
  if ($[2] !== chat) {
    t2 = {
      chat
    };
    $[2] = chat;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const helpers = useChat(t2);
  const {
    isHydrating,
    reloadMessages
  } = useThreadHydration(threadId, chat, helpers.setMessages);
  let t3;
  if ($[4] !== helpers || $[5] !== isHydrating || $[6] !== reloadMessages) {
    t3 = Object.assign(helpers, {
      isHydrating,
      reloadMessages
    });
    $[4] = helpers;
    $[5] = isHydrating;
    $[6] = reloadMessages;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  return t3;
}

/**
 * Fetch a thread's transcript from Postgres the first time it's opened and feed
 * it into the shared `Chat`. Skips the draft, already-hydrated threads, and any
 * thread that already holds messages (promoted / live) so it never overwrites a
 * live transcript. On failure the thread is un-marked so a later open retries.
 */
function useThreadHydration(threadId, chat, setMessages) {
  const $ = _c(16);
  const trpcClient = useTRPCClient();
  const [isHydrating, setIsHydrating] = useState(false);
  const [reloadNonce, setReloadNonce] = useState(0);
  let t0;
  if ($[0] !== threadId) {
    t0 = () => {
      if (threadId && threadId !== "__draft__") {
        hydratedThreads.delete(threadId);
      }
      setReloadNonce(_temp);
    };
    $[0] = threadId;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const reloadMessages = t0;
  let t1;
  if ($[2] !== chat || $[3] !== setMessages || $[4] !== threadId || $[5] !== trpcClient) {
    t1 = () => {
      if (!threadId || threadId === "__draft__") {
        setIsHydrating(false);
        return;
      }
      if (hydratedThreads.has(threadId)) {
        setIsHydrating(false);
        return;
      }
      if (chat.messages.length > 0) {
        hydratedThreads.add(threadId);
        setIsHydrating(false);
        return;
      }
      hydratedThreads.add(threadId);
      setIsHydrating(true);
      let cancelled = false;
      trpcClient.aiChat.getMessages.query({
        id: threadId
      }).then(messages => {
        if (cancelled) {
          return;
        }
        setIsHydrating(false);
        if (chat.messages.length > 0) {
          return;
        }
        if (messages.length === 0) {
          hydratedThreads.delete(threadId);
          return;
        }
        setMessages(messages);
      }).catch(() => {
        hydratedThreads.delete(threadId);
        if (!cancelled) {
          setIsHydrating(false);
        }
      });
      return () => {
        cancelled = true;
      };
    };
    $[2] = chat;
    $[3] = setMessages;
    $[4] = threadId;
    $[5] = trpcClient;
    $[6] = t1;
  } else {
    t1 = $[6];
  }
  let t2;
  if ($[7] !== chat || $[8] !== reloadNonce || $[9] !== setMessages || $[10] !== threadId || $[11] !== trpcClient) {
    t2 = [threadId, chat, setMessages, trpcClient, reloadNonce];
    $[7] = chat;
    $[8] = reloadNonce;
    $[9] = setMessages;
    $[10] = threadId;
    $[11] = trpcClient;
    $[12] = t2;
  } else {
    t2 = $[12];
  }
  useEffect(t1, t2);
  let t3;
  if ($[13] !== isHydrating || $[14] !== reloadMessages) {
    t3 = {
      isHydrating,
      reloadMessages
    };
    $[13] = isHydrating;
    $[14] = reloadMessages;
    $[15] = t3;
  } else {
    t3 = $[15];
  }
  return t3;
}

/**
 * Move the in-memory messages from the draft chat onto a freshly-created thread
 * id. Called the first time the user sends a message from a "no thread yet" surface.
 */
function _temp(n) {
  return n + 1;
}
export function promoteDraftChat(newThreadId: string): void {
  const draft = chatRegistry.get('__draft__');
  if (!draft) return;
  // `Chat` extends `AbstractChat`, which exposes a `messages` setter at
  // runtime — `setMessages` lives on the `useChat` hook return, not on the
  // `Chat` instance. Snapshot the array before reassigning so the source
  // and target don't share the same reference once we clear the draft.
  const snapshot = [...draft.messages];
  draft.messages = [];
  // Nothing made it onto the draft — e.g. the user hit Back (`goToAllChats` →
  // `clearDraftChat`) before the in-flight stream's `finally` promoted it. Do
  // NOT create an empty thread or mark it hydrated: that would make a later
  // `openThread` skip the Postgres refetch and land on the "couldn't load"
  // state. Leaving it un-hydrated lets the reopen recover the persisted turn.
  if (snapshot.length === 0) return;
  const target = getOrCreateChat(newThreadId);
  target.messages = snapshot;
  // Carry the draft's terminal status/error onto the promoted thread. A first
  // turn that errors (e.g. a 429 rate limit) sets `error` on the DRAFT `Chat`,
  // but the panel re-binds to `newThreadId` after promotion — without this the
  // error bubble never renders for the very first message of a new chat.
  if (draft.status === 'error') {
    target.applyTerminalError(draft.error);
  }
  // The promoted thread now holds the live transcript — mark it hydrated so
  // `useThreadHydration` never overwrites it with a stale/empty DB read while
  // the server-side persist is still in flight.
  hydratedThreads.add(newThreadId);
}

/**
 * Send a message into a specific thread's `Chat` instance without needing the
 * caller to be the `useChat` consumer for that thread. Used by "start new chat"
 * flows where the active hook is still bound to a different thread id.
 */
export function sendMessageToThread(threadId: string, text: string, options?: {
  body?: Record<string, unknown>;
}): Promise<void> {
  const chat = getOrCreateChat(threadId);
  return chat.sendMessage({
    text
  }, options);
}

/**
 * Reset the draft chat instance so the "all chats" landing surface starts fresh.
 * Called when the user navigates back from a draft thread that was never
 * promoted (no real thread id yet).
 */
export function clearDraftChat(): void {
  const draft = chatRegistry.get('__draft__');
  if (!draft) return;
  draft.messages = [];
}

/**
 * Test-only helpers. Exposed so unit tests can exercise `promoteDraftChat` and
 * the registry without rendering a React tree. Not part of the public API —
 * production code MUST NOT import these.
 */
export const __testHelpers = {
  getOrCreateChat,
  resetRegistry: () => {
    chatRegistry.clear();
    hydratedThreads.clear();
  },
  isHydrated: (id: string) => hydratedThreads.has(id)
};